import React, { useLayoutEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentJob } from '../contexts/LayoutContext';
import { ICONS } from './ui';
import {
  SETTINGS_ITEMS, JOB_SCREENS, homePath, jobTabsFor,
  visibleSections, visibleItems, sectionForPath, sectionLanding,
} from '../nav/sections';
import { jobPath } from '../nav/paths';

// The help for the current screen (the ? icon opens it): the main Help at the
// matching section, or the Receivables Help.
const RECEIVABLES_HELP = '/help-invoicing';
const JOB_TAB_HELP = { list: 'job-page', budget: 'jobs-budget', entries: 'jobs-entries', invoicing: 'jobs-invoicing', approval: 'jobs-approval' };
function helpPathFor(pathname) {
  const parts = pathname.split('/');
  const section = (() => {
    if (pathname.startsWith('/dashboard')) return 'dashboard';
    if (pathname.startsWith('/time/timesheets/')) return 'time-views';
    if (pathname.startsWith('/analytics/reports')) return 'reports';
    if (pathname.startsWith('/analytics')) return 'billable';
    if (pathname.startsWith('/time')) return 'time-entry';
    if (pathname.startsWith('/expenses')) return 'expenses';
    if (pathname.startsWith('/receivables')) return RECEIVABLES_HELP;
    if (parts[1] === 'jobs') {
      if (!parts[2] || JOB_SCREENS.includes(parts[2])) return JOB_TAB_HELP[parts[2] || 'list'];
      if (parts[3] === 'receivables') return RECEIVABLES_HELP;
      return JOB_TAB_HELP[parts[3]] || 'job-page';
    }
    if (pathname.startsWith('/projections')) return 'budget';
    if (pathname.startsWith('/settings')) return 'advanced';
    return null;
  })();
  if (section === RECEIVABLES_HELP) return RECEIVABLES_HELP;
  return section ? `/help?section=${section}` : '/help';
}

// The sub-nav item for a path: the longest item path that matches it.
function activeItem(items, pathname) {
  return items
    .filter((i) => pathname === i.path || pathname.startsWith(i.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0] || null;
}

// The sub-nav tabs for the current screen: a job page's tabs, Settings' tabs,
// or the current section's screens.
function subNavFor(user, pathname, currentJob) {
  const parts = pathname.split('/');
  if (parts[1] === 'jobs' && parts[2] && !JOB_SCREENS.includes(parts[2])) {
    const jobNo = decodeURIComponent(parts[2]);
    const isOverhead = !!(currentJob && currentJob.jobNo === jobNo && currentJob.isOverhead);
    return {
      label: jobNo,
      items: jobTabsFor(user, isOverhead).map((t) => ({ ...t, path: jobPath(jobNo, t.key) })),
    };
  }
  if (parts[1] === 'settings') return { items: visibleItems(user, SETTINGS_ITEMS) };
  const section = sectionForPath(pathname);
  return { items: section ? visibleItems(user, section.items) : [] };
}

// One sticky header for every screen: the navbar (brand, task sections, user
// icons), the emulation banner, and the current section's sub-nav. Its height is
// reported through onHeight and published as the --app-header-h CSS variable.
export default function AppHeader({ onHeight }) {
  const { user, logout, stopEmulation } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ref = useRef(null);
  const isEmulating = !!user?.emulating;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const report = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--app-header-h', `${h}px`);
      if (onHeight) onHeight(h);
    };
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeight]);

  const sections = visibleSections(user);
  const current = sectionForPath(pathname);
  const { job: currentJob } = useCurrentJob();
  const sub = subNavFor(user, pathname, currentJob);
  const subActive = activeItem(sub.items, pathname);
  const showSub = sub.label || sub.items.length > 0;

  const openHelp = () => navigate(helpPathFor(pathname));

  const handleStopEmulation = async () => {
    await stopEmulation();
    navigate('/');
  };

  return (
    <header className="app-header" ref={ref}>
      <nav className={`navbar ${isEmulating ? 'navbar-emulating' : ''}`}>
        <div className="app-header-inner navbar-inner">
          <h1 className="navbar-brand" onClick={() => navigate(homePath(user))}>RCPTime 2026</h1>
          <div className="nav-sections">
            {sections.map((s) => (
              <NavLink
                key={s.key}
                to={sectionLanding(user, s)}
                className={() => `nav-section${current && current.key === s.key ? ' active' : ''}`}
                title={s.label}
              >
                <span className="nav-label-full">{s.label}</span>
                <span className="nav-label-short">{s.icon ? ICONS[s.icon] : (s.short || s.label)}</span>
                {s.badge && <span className="nav-badge">{s.badge}</span>}
              </NavLink>
            ))}
          </div>
          <div className="navbar-user">
            <span className="navbar-name">{user?.displayName?.split(' ')[0]}</span>
            <button className={`navbar-btn navbar-help-btn${pathname.startsWith('/settings') ? ' active' : ''}`}
              onClick={() => navigate('/settings')} title="Settings" aria-label="Settings">
              {ICONS.settings}
            </button>
            <button className={`navbar-btn navbar-help-btn${pathname.startsWith('/help') ? ' active' : ''}`}
              onClick={openHelp} title="Help" aria-label="Help">
              {ICONS.help}
            </button>
            <button className="navbar-btn navbar-help-btn" onClick={logout} title="Log out" aria-label="Log out">
              {ICONS.logout}
            </button>
          </div>
        </div>
      </nav>

      {isEmulating && (
        <div className="emulation-banner">
          <div className="app-header-inner emulation-banner-inner">
            <span>
              Emulating: <strong>{user.emulating.displayName}</strong>
              <span style={{ opacity: 0.8, marginLeft: '6px' }}>({user.emulating.resourceNo})</span>
            </span>
            <button className="emulation-stop-btn" onClick={handleStopEmulation}>Stop</button>
          </div>
        </div>
      )}

      {showSub && (
        <div className="sub-nav">
          <div className="app-header-inner sub-nav-inner">
            {sub.label && <span className="sub-nav-label">{sub.label}</span>}
            {sub.items.map((i) => (
              <NavLink key={i.key} to={i.path}
                className={() => `sub-nav-item${subActive && subActive.key === i.key ? ' active' : ''}`} title={i.label}>
                <span className="nav-label-full">{i.label}</span>
                <span className="nav-label-short">{i.short || i.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
